// Session options for a RUNNING session: what used to be the controls bar
// across the top of the chat (width toggle, the badge that says the chat's
// process is live, and the ⋮ menu with Stop), moved into one dialog opened from the gear in the
// composer — the same gear the start screen has (Matt, 11 Sep, #1347 item 5).
// The conversation gets the bar's height back.
//
// `keepMounted` is not cosmetic: the process control inside polls the chat's
// status and reports its live state to the page (for the History badge), and
// that must run whether or not the dialog is open.

import React from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import type { AudioDevice } from '../../agent'
import MicrophoneSelect from './MicrophoneSelect'

export type ChatWidth = 'column' | 'full'

interface SessionSettingsDialogProps {
  open: boolean
  onClose: () => void
  chatWidth: ChatWidth
  onChatWidthChange: (w: ChatWidth) => void
  /** The chat's live badge and ⋮ menu (Stop). Rendered whether open or not. */
  sessionControl?: React.ReactNode
  /** The microphone choice for dictation (Adam, 14 Sep); absent hides the row. */
  microphones?: AudioDevice[]
  microphoneId?: string | null
  onMicrophoneChange?: (deviceId: string | null) => void
  /**
   * The chat's mode when it is not Normal ("Chief of Staff"); absent draws no
   * Mode section. Read-only: a chat's profile is fixed when it is made
   * (design/2026-10-26-chief-of-staff-agents-panel.md T1).
   */
  modeLabel?: string
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>{title}</Typography>
      {children}
    </Box>
  )
}

export default function SessionSettingsDialog({ open, onClose, chatWidth, onChatWidthChange, sessionControl, microphones, microphoneId, onMicrophoneChange, modeLabel }: SessionSettingsDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      keepMounted
      maxWidth="xs"
      fullWidth
      data-testid="agent-session-settings"
      // Kept mounted while closed, so it must not READ as a dialog then: a
      // second role="dialog" in the DOM breaks every "the dialog" locator on
      // the page (e2e 24-agent-artifact found two). Closed, the paper is
      // hidden and carries no role.
      PaperProps={{ role: open ? 'dialog' : 'none' }}
    >
      <DialogTitle sx={{ fontSize: 16 }}>Session options</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {sessionControl && (
          <Section title="Session">
            <Box data-testid="agent-session-settings-control" sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 34 }}>
              {sessionControl}
            </Box>
          </Section>
        )}
        {modeLabel && (
          <Section title="Mode">
            <Typography data-testid="agent-session-settings-mode" sx={{ fontSize: 14 }}>{modeLabel}</Typography>
          </Section>
        )}
        <Section title="Layout">
          <ToggleButtonGroup
            exclusive
            size="small"
            color="info"
            value={chatWidth}
            onChange={(_e, v: ChatWidth | null) => { if (v) onChatWidthChange(v) }}
            aria-label="Chat width"
          >
            <ToggleButton value="column" aria-label="Column width" sx={{ textTransform: 'none', gap: 0.75, px: 1.5 }}>
              <CloseFullscreenIcon sx={{ fontSize: 16 }} /> Column
            </ToggleButton>
            <ToggleButton value="full" aria-label="Full width" sx={{ textTransform: 'none', gap: 0.75, px: 1.5 }}>
              <OpenInFullIcon sx={{ fontSize: 16 }} /> Full width
            </ToggleButton>
          </ToggleButtonGroup>
        </Section>
        {microphones && onMicrophoneChange && (
          <Section title="Dictation">
            <MicrophoneSelect microphones={microphones} microphoneId={microphoneId ?? null} onMicrophoneChange={onMicrophoneChange} />
          </Section>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="info" onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}
